import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { api } from "helpers/axios";
import { useOutletContext } from "react-router-dom";
import plusSlashMinusIcon from "assets/images/icons/plus-slash-minus-icon.png";
import { errorMessage } from "helpers/common";
import * as Sentry from "@sentry/react";
import ErrorAlert from "components/ui/ErrorAlert";
import Link from "components/ui/Link";
import { Button, Modal } from "components/ui";
import { useTranslation } from "translation/useTranslation";

function CalculationRow({ calculation, handleDelete, setActiveCalculationSection }) {
  const { editableTranslate } = useTranslation();

  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleShowConfirm = (e) => {
    e.preventDefault();

    if (!loading) {
      setLoading(false);
      setShowConfirm(true);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setShowConfirm(false);
  };

  const handleClickDelete = async (e) => {
    e.preventDefault();
    setLoading(true);
    await handleDelete(calculation.hash_id);
    setLoading(false);
    setShowConfirm(false);
  };

  return <div className="col-xxl-4 col-6 mb-4" data-testid={`calculation-card-${calculation.hash_id}`}>
    <div className="peb-summary-card">
      <div className="card-setting-header d-flex justify-content-between">
        <span className="max-50">{calculation.name}</span>
        <div className="max-50 text-right">
          <Button variant="ghost" size="inline" tone="danger" icon="bi-trash" onClick={handleShowConfirm} data-testid="delete-button">{editableTranslate('global.delete')}</Button>
          <Button data-testid="edit-button" as={Link} variant="ghost" size="inline" icon="bi-pencil-square" onClick={() => setActiveCalculationSection(null)} to={`/settings/calculation/form/${calculation.hash_id}`}>{editableTranslate('global.edit')}</Button>
        </div>
      </div>
      <div className="card-setting-body mt-3">
        <div className="row">
          <div className="col-6 card-body-key">{editableTranslate('calculation_settings.calculation_method')}</div>
          <div className="col-6 card-body-value">{editableTranslate('calculation_settings.' + calculation.full_settings.calculation_method)}</div>

          <div className="col-6 card-body-key">{editableTranslate('calculation_settings.should_prorate_pay_parttime_employees')}</div>
          <div className="col-6 card-body-value text-capitalize">{editableTranslate((calculation.full_settings.should_prorate_pay_parttime_employees === 'yes' ? 'form.yes' : 'form.no'))}</div>

          <div className="col-6 card-body-key">{editableTranslate('calculation_settings.define_how_rest_periods_affect')}</div>
          <div className="col-6 card-body-value">{calculation.full_settings.how_rest_periods_affect_calculation ? editableTranslate('calculation_settings.how_rest_periods_affect.no_more_distributions') + ' ' + calculation.full_settings.how_rest_periods_affect_calculation_amount : '-'}</div>

          <div className="col-6 card-body-key">{editableTranslate('calculation_settings.should_min_employment_specified')}</div>
          <div className="col-6 card-body-value">{calculation.full_settings.how_many_months_at_least_amount} {editableTranslate('form.months')}</div>

          <div className="col-6 card-body-key">{editableTranslate('calculation_settings.should_premium_cap_defined')}</div>
          <div className="col-6 card-body-value text-capitalize">{editableTranslate((calculation.full_settings.should_premium_cap_defined === 'yes' ? 'form.yes' : 'form.no'))}</div>

          <div className="col-6 card-body-key">{editableTranslate('calculation_settings.benefit_distribution_type')}</div>
          <div className="col-6 card-body-value">{editableTranslate('calculation_settings.benefit_distribution_type.' + calculation.full_settings.benefit_distribution_type)}</div>

          <div className="col-6 card-body-key">{editableTranslate('calculation_settings.shoud_use_fixed_amount_distribution')}</div>
          <div className="col-6 card-body-value text-capitalize">{editableTranslate((calculation.full_settings.shoud_use_fixed_amount_distribution === 'yes' ? 'form.yes' : 'form.no'))}</div>
        </div>
      </div>
    </div>
    <Modal
      open={showConfirm}
      danger
      loading={loading}
      title={editableTranslate('calculation_settings.delete_confirmation_title', 'Berechnungsmodell löschen?')}
      cancelLabel={editableTranslate('form.no')}
      okLabel={editableTranslate('form.delete.yes')}
      onClose={handleCancel}
      onConfirm={handleClickDelete}
      testIdPrefix="calculation-delete-"
    >
      {editableTranslate('calculation_settings.delete_confirmation')}
    </Modal>
  </div>
}

export default function Calculation() {
  const { setActiveCalculationSection } = useOutletContext();

  const dispatch = useDispatch();

  const { editableTranslate } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [calculations, setCalculations] = useState([]);

  const fetchCalculations = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data, status } = await api.get(`/settings/calculation`);
      if (status === 200) {
        setCalculations(data.data);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setError(errorMessage(err, 'fetch calculation settings'));
      Sentry.captureException(err);
    }
  };

  useEffect(() => {
    fetchCalculations();
  }, []);

  const handleDelete = async (hashId) => {
    setError(null);
    try {
      const { status } = await api.delete(`/settings/calculation/${hashId}`);
      if (status === 200) {
        fetchCalculations();
      }
    } catch (err) {
      setError(errorMessage(err, 'delete calculation setting'));
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <div className="d-flex justify-content-between">
            <h4 className="mb-0">{editableTranslate('global.menu.settings.calculation')}</h4>
            <Button data-testid="add-button" as={Link} variant="secondary" onClick={() => setActiveCalculationSection(null)} to="/settings/calculation/form">
              <i className="bi bi-plus"></i> {editableTranslate('calculation_settings.add_calculation_model')}
            </Button>
          </div>

          {
            loading
              ? <div className="text-center" data-testid="loading">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Wird geladen...</span>
                </div>
              </div>
              : <>
                <div className="mt-3">
                  {
                    error && <div>
                      <ErrorAlert error={error} onClose={() => setError(null)} />
                    </div>
                  }

                  <div className="row row-eq-height">
                    {
                      calculations.map((calculation, i) => {
                        return <CalculationRow key={i} calculation={calculation} handleDelete={handleDelete} setActiveCalculationSection={setActiveCalculationSection} />
                      })
                    }
                  </div>
                  {
                    calculations.length === 0 && <div className="calculation-setting-empty">
                      <img src={plusSlashMinusIcon} />
                      <p>{editableTranslate('calculation_settings.no_model')} <span>'{editableTranslate('calculation_settings.add_calculation_model')}'</span> {editableTranslate('form.button')}.</p>
                    </div>
                  }
                </div>
              </>
          }
        </div>
      </div>
    </>
  );
}
